import { useLenis } from 'lenis/react'
import { lazy, useEffect, useRef, useState, type ReactNode } from 'react'
import { Button, Eyebrow, Icon, Lang, Magnetic, Segments } from '@/components/ui'
import { hero, manifesto } from '@/content/site'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { useScrollProgress } from '@/hooks/useScrollProgress'
import { EASE, gsap, SplitText, useGSAP } from '@/lib/gsap'
import { useAppStore } from '@/lib/store'
import { hasWebGL2 } from '@/webgl/capabilities'
import { loadSceneRenderer } from '@/webgl/loaders'
import { SceneCanvas } from '@/webgl/SceneCanvas'
import { withRetry } from '@/webgl/retry'
import styles from './Hero.module.css'
import { HoldToTalk } from './HoldToTalk'
import { SpeakChip } from './SpeakChip'
import { createHeroState } from './state'

/** Matches the CSS: phones, and tall tablets in portrait, stack the orb above the text. */
const STACKED_QUERY = '(max-width: 767px), (max-width: 1023px) and (max-aspect-ratio: 4/5)'
const loadHeroScene = () => import('./HeroScene')
const HeroScene = lazy(() =>
  withRetry(loadHeroScene).then((module) => ({ default: module.HeroScene })),
)

// The hero is the first thing on screen, so its renderer and scene start downloading as
// soon as this module runs, in parallel with the first render, instead of after its
// canvas has been laid out and observed. Failures are left to the lazy retries above.
if (hasWebGL2()) {
  void loadSceneRenderer().catch(() => undefined)
  void loadHeroScene().catch(() => undefined)
}

/** Wraps the romanised "kartā" in a meta line with its language. */
function withKarta(text: string): ReactNode {
  const word = manifesto.karta.latin
  const at = text.indexOf(word)
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      <Lang code="hi-Latn">{word}</Lang>
      {text.slice(at + word.length)}
    </>
  )
}

/**
 * Hero: the Living Voice Orb. A clear glass orb refracts a giant slate VOKARTA in a
 * daylight studio; the DOM carries the real H1 ("Talk. Don't type."), the lead and the
 * CTAs. The DOM reserves a "stage" for the 3D and the scene solves its layout from it, so
 * type and glass never collide on any screen.
 */
export function Hero() {
  const sectionRef = useRef<HTMLElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const scroll = useScrollProgress(sectionRef, { start: 'top top', end: 'bottom top' })
  const [state] = useState(() => createHeroState(scroll))
  const reducedMotion = useReducedMotion()
  const introStarted = useAppStore((s) => s.phase !== 'boot')
  const sceneReady = useAppStore((s) => Boolean(s.readyScenes.hero))

  useLenis((lenis) => {
    state.scrollVelocity = lenis.velocity
  })

  // Measure the stage the DOM leaves for the orb; the scene builds its layout from it.
  useEffect(() => {
    const section = sectionRef.current
    const stage = stageRef.current
    if (!section || !stage) return
    const stacked = window.matchMedia(STACKED_QUERY)
    const measure = () => {
      const box = section.getBoundingClientRect()
      const room = stage.getBoundingClientRect()
      const layout = state.layout
      layout.width = box.width
      layout.height = box.height
      layout.stageTop = room.top - box.top
      layout.stageBottom = room.bottom - box.top
      layout.stacked = stacked.matches
      layout.version += 1
      state.rect.top = box.top + window.scrollY
      state.rect.left = box.left + window.scrollX
      state.invalidate?.()
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(section)
    observer.observe(stage)
    stacked.addEventListener('change', measure)
    let alive = true
    void document.fonts.ready.then(() => alive && measure())
    return () => {
      alive = false
      observer.disconnect()
      stacked.removeEventListener('change', measure)
    }
  }, [state])

  // DOM intro: masked headline lines, staggered meta, lead and actions (about 2s).
  useGSAP(
    () => {
      const root = sectionRef.current
      if (!root) return
      const items = (group: string) =>
        gsap.utils.toArray<HTMLElement>(`[data-intro="${group}"]`, root)
      const lines = gsap.utils.toArray<HTMLElement>('[data-line]', root)
      const all = gsap.utils.toArray<HTMLElement>('[data-intro]', root)
      if (!introStarted) {
        gsap.set([...all, ...lines], { autoAlpha: 0 })
        return
      }
      if (reducedMotion) {
        gsap.fromTo(
          [...all, ...lines],
          { autoAlpha: 0 },
          { autoAlpha: 1, duration: 0.3, ease: 'none' },
        )
        return
      }
      lines.forEach((line, index) => {
        SplitText.create(line, {
          type: 'lines',
          mask: 'lines',
          tag: 'span',
          aria: 'none',
          autoSplit: true,
          onSplit: (self) =>
            gsap.from(self.lines, {
              yPercent: 108,
              duration: 1.3,
              delay: 0.12 + index * 0.11,
              ease: EASE.out,
            }),
        })
      })
      const tl = gsap.timeline({ defaults: { ease: EASE.out } })
      tl.from(items('top'), { y: 14, autoAlpha: 0, duration: 1.1, stagger: 0.08 }, 0)
        .from(items('chip'), { y: 10, autoAlpha: 0, duration: 1.1 }, 0.62)
        .from(items('foot'), { y: 22, autoAlpha: 0, duration: 1.2, stagger: 0.08 }, 0.72)
        .from(items('cue'), { autoAlpha: 0, duration: 1.2 }, 1.15)
    },
    { scope: sectionRef, dependencies: [introStarted, reducedMotion], revertOnUpdate: true },
  )

  // 3D intro: the orb resolves from a small frosted state, letters rise, the floor wakes.
  useGSAP(
    () => {
      const intro = state.intro
      if (reducedMotion) {
        intro.orb = 1
        intro.floor = 1
        intro.letters.forEach((letter) => (letter.value = 1))
        state.invalidate?.()
        return
      }
      if (!introStarted || !sceneReady) return
      gsap.fromTo(intro, { orb: 0 }, { orb: 1, duration: 2.4, ease: EASE.out })
      gsap.fromTo(intro, { floor: 0 }, { floor: 1, duration: 2.2, delay: 0.45, ease: EASE.out })
      gsap.fromTo(
        intro.letters,
        { value: 0 },
        { value: 1, duration: 1.6, delay: 0.2, stagger: 0.07, ease: EASE.out },
      )
    },
    { dependencies: [introStarted, sceneReady, reducedMotion, state] },
  )

  // Scroll-out: the overlay lifts and fades (the 3D side is scrubbed in the scene).
  useGSAP(
    () => {
      const overlay = overlayRef.current
      if (reducedMotion || !overlay) return
      gsap
        .timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 1,
          },
        })
        .to(overlay, { yPercent: -9, ease: 'none', duration: 1 }, 0)
        .to(overlay, { autoAlpha: 0, ease: 'none', duration: 0.5 }, 0.22)
    },
    { scope: sectionRef, dependencies: [reducedMotion], revertOnUpdate: true },
  )

  const [line1, line2] = hero.title

  return (
    <section
      ref={sectionRef}
      id="hero"
      aria-labelledby="hero-title"
      className={styles.root}
      data-theme="light"
    >
      <div className={styles.canvas}>
        <SceneCanvas
          id="hero"
          label={hero.sceneLabel}
          flat
          gl={{ alpha: false }}
          camera={{ position: [0, 0.62, 9.2], fov: 26, near: 0.1, far: 80 }}
        >
          <HeroScene state={state} />
        </SceneCanvas>
      </div>

      <div ref={overlayRef} className={styles.overlay}>
        <div className={styles.metaRow}>
          <p className={styles.meta} data-intro="top">
            {hero.meta[0]}
          </p>
          <p className={`${styles.meta} ${styles.metaEnd}`} data-intro="top">
            {withKarta(hero.meta[1])}
          </p>
        </div>

        <div className={styles.head}>
          <Eyebrow data-intro="top">{hero.eyebrow}</Eyebrow>
          <h1 id="hero-title" className={styles.title}>
            <span className={styles.line}>
              <span className={styles.lineText} data-line>
                <Segments segments={line1} />
              </span>
              <SpeakChip state={state} reducedMotion={reducedMotion} />
            </span>
            <span className={styles.line}>
              <span className={styles.lineText} data-line>
                <Segments segments={line2} />
              </span>
            </span>
          </h1>
        </div>

        <div ref={stageRef} className={styles.stage} aria-hidden="true" />

        <div className={styles.foot}>
          <p className={styles.kicker} data-intro="foot">
            {hero.kicker}
          </p>
          <p className={styles.lead} data-intro="foot">
            {hero.lead}
          </p>
          <div className={styles.actions} data-intro="foot">
            <Magnetic>
              <Button href={hero.primary.href} external size="lg" icon="arrow-up-right">
                {hero.primary.label}
              </Button>
            </Magnetic>
            <Button variant="link" href={hero.secondary.href} icon="arrow-down">
              {hero.secondary.label}
            </Button>
          </div>
        </div>

        <div className={styles.holdSlot} data-intro="cue">
          <HoldToTalk state={state} />
        </div>

        <p className={styles.cue} data-intro="cue">
          <span className={styles.cueLine} aria-hidden="true" />
          {hero.meta[2]}
          <Icon name="arrow-down" />
        </p>
      </div>
    </section>
  )
}
